import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'react-use';
import { Common } from '@thecvlb/design-system/lib/src';

import TextInput from 'shared/form/TextInput';
import { FormValues } from 'widgets/Insurance/insurance.types';

import { MEMBER_ID_REGEXP } from 'utils/regExp';

import { SelectInsuranceProvider } from 'models/insurance.types';

type FormFields = FormValues & { isPrimaryHolder: boolean; payerId: string; provider: string };

type Props = {
  continueButtonText?: string;
  defaultValues?: FormFields[];
  disabled?: boolean;
  loading?: boolean;
  onSkip: () => void;
  onSubmit: (value: FormFields[]) => void;
  providers?: SelectInsuranceProvider[];
};

const AddInsuranceForm = ({
  disabled,
  loading,
  onSubmit,
  onSkip,
  defaultValues,
  continueButtonText = 'Continue',
  providers = []
}: Props) => {
  const { control, handleSubmit, reset } = useForm<FormValues>({
    criteriaMode: 'all',
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const [isPrimaryHolderCheck, toggleCheck] = useToggle(true);
  const [isPrimaryHolderCheckSecondary, toggleCheckSecondary] = useToggle(true);
  const [doesHaveSecondaryInsurance, toggleAddSecondaryInsurance] = useToggle(false);
  const [insuranceProvider, setSelectedProvider] = useState('');
  const [insuranceProviderSecondary, setSelectedProviderSecondary] = useState('');

  const handleFormSubmit = (v: FormValues & { providerName?: string }) => {
    const label = providers.find((i) => i.value === insuranceProvider)?.label ?? '';
    if (!doesHaveSecondaryInsurance) {
      const body = {
        ...v,
        isPrimaryHolder: isPrimaryHolderCheck,
        payerId: insuranceProvider,
        provider: insuranceProvider === 'other' ? (v.providerName ?? '') : label
      };
      onSubmit([body]);
    } else {
      const primaryInsurance = Object.fromEntries(
        Object.entries(v).filter(([key]) => !key.includes('secondary'))
      );
      const secondaryInsurance = Object.fromEntries(
        Object.entries(v)
          .filter(([key]) => key.includes('secondary'))
          .map(([key, value]) => [key.replace('_secondary', ''), value])
      );
      const label = providers.find((i) => i.value === insuranceProvider)?.label ?? '';
      const labelSecondary =
        providers.find((i) => i.value === insuranceProviderSecondary)?.label ?? '';

      const body = [
        {
          ...(primaryInsurance as FormValues),
          isPrimaryHolder: isPrimaryHolderCheck,
          payerId: insuranceProvider,
          provider: insuranceProvider === 'other' ? (v.providerName ?? '') : label
        },
        {
          ...(secondaryInsurance as FormValues),
          isPrimaryHolder: isPrimaryHolderCheckSecondary,
          payerId: insuranceProviderSecondary,
          provider: insuranceProviderSecondary === 'other' ? (v.providerName ?? '') : labelSecondary
        }
      ];
      onSubmit(body);
    }
  };

  useEffect(() => {
    if (defaultValues) {
      const additionalInsurance = defaultValues[1];
      const objToReset: Record<string, unknown> = {};
      if (additionalInsurance) {
        toggleAddSecondaryInsurance(true);
        for (let key in additionalInsurance) {
          objToReset[key + '_secondary'] = additionalInsurance[key as keyof FormValues];
        }
      }
      if (objToReset['payerId_secondary'] && typeof objToReset['payerId_secondary'] === 'string') {
        setSelectedProviderSecondary(objToReset['payerId_secondary']);
      }

      reset({ ...defaultValues[0], ...objToReset });

      if (defaultValues[0]?.payerId) {
        setSelectedProvider(defaultValues[0].payerId);
      }
    }
  }, []);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="flex flex-col gap-4">
        <h3 className="font-bold text-primary-700">Insurance</h3>
        <Common.Checkbox checked={isPrimaryHolderCheck} color="blue" onChange={toggleCheck}>
          <span className="text-base font-bold text-gray-700">
            I am the primary account holder for this plan.
          </span>
        </Common.Checkbox>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-3">
            <Common.SelectAlt
              dataTestId="insurance_plan_select"
              label="Insurance plan"
              options={providers}
              placeholder="Select your plan from the list..."
              value={insuranceProvider}
              hideSuccessState
              onChange={setSelectedProvider}
            />
          </div>
          {insuranceProvider === 'other' && (
            <div className="md:col-span-3">
              <TextInput
                control={control}
                disabled={loading}
                maxLength={20}
                name="providerName"
                placeholder="Other provider..."
                requiredErrorMsg="Field is required"
              />
            </div>
          )}
          {!!insuranceProvider && (
            <>
              <div className="md:col-span-3">
                <TextInput
                  control={control}
                  dataTestId="member_id_input"
                  disabled={loading}
                  invalidErrorMsg="Must be non-empty and using only letters, numbers, - or _."
                  label="Member ID"
                  maxLength={20}
                  name="memberId"
                  placeholder="12345678900"
                  regExp={MEMBER_ID_REGEXP}
                  requiredErrorMsg="Field is required"
                />
              </div>
              <div className="md:col-span-3">
                <TextInput
                  control={control}
                  disabled={loading}
                  invalidErrorMsg="Must be non-empty and using only numbers"
                  label="Group Number"
                  maxLength={20}
                  name="groupId"
                  placeholder="12345678900"
                  regExp={MEMBER_ID_REGEXP}
                  requiredErrorMsg="Field is required"
                />
              </div>
              <div>
                <TextInput
                  control={control}
                  dataTestId="rx_bin_input"
                  disabled={loading}
                  invalidErrorMsg="Must be non-empty and using only letters, numbers, - or _."
                  label="RX BIN"
                  name="rxBIN"
                  placeholder="0012345"
                  regExp={MEMBER_ID_REGEXP}
                  requiredErrorMsg="Field is required"
                />
              </div>
              <div>
                <TextInput
                  control={control}
                  dataTestId="rx_group_input"
                  disabled={loading}
                  invalidErrorMsg="RX GROUP is invalid"
                  isRequired={false}
                  label="RX Group"
                  name="rxGroupId"
                  placeholder="A1AA"
                  regExp={MEMBER_ID_REGEXP}
                  requiredErrorMsg="Field is required"
                />
              </div>
              <div>
                <TextInput
                  control={control}
                  dataTestId="pcn_input"
                  disabled={loading}
                  invalidErrorMsg="PCN is invalid"
                  isRequired={false}
                  label="PCN"
                  name="rxPCN"
                  placeholder="A1"
                  regExp={MEMBER_ID_REGEXP}
                  requiredErrorMsg="Field is required"
                />
              </div>
              <div className="mt-2 md:col-span-3">
                <Common.Checkbox
                  checked={doesHaveSecondaryInsurance}
                  color="blue"
                  onChange={toggleAddSecondaryInsurance}
                >
                  <span className="text-base text-gray-700">Add additional insurance</span>
                </Common.Checkbox>
              </div>
            </>
          )}
        </div>
      </div>
      {doesHaveSecondaryInsurance && (
        <div className="flex flex-col gap-4 rounded-2xl border bg-gray-100 p-3 md:p-4">
          <h3 className="font-bold text-primary-700">Secondary insurance</h3>
          <Common.Checkbox
            checked={isPrimaryHolderCheckSecondary}
            color="blue"
            onChange={toggleCheckSecondary}
          >
            <span className="text-base font-bold text-gray-700">
              I am the primary account holder for this plan.
            </span>
          </Common.Checkbox>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-3">
              <Common.SelectAlt
                dataTestId="additional_insurance_plan_select"
                label="Insurance plan"
                loading={loading}
                options={providers}
                placeholder="Select your plan from the list..."
                value={insuranceProviderSecondary}
                hideSuccessState
                onChange={setSelectedProviderSecondary}
              />
            </div>
            {insuranceProviderSecondary === 'other' && (
              <div className="md:col-span-3">
                <TextInput
                  control={control}
                  disabled={loading}
                  invalidErrorMsg="Must be non-empty and using only letters, numbers, - or _."
                  maxLength={20}
                  name="providerName_secondary"
                  placeholder="Other provider..."
                  regExp={MEMBER_ID_REGEXP}
                  requiredErrorMsg="Field is required"
                />
              </div>
            )}
            <div className="md:col-span-3">
              <TextInput
                control={control}
                dataTestId="member_id_input"
                disabled={loading}
                invalidErrorMsg="Must be non-empty and using only letters, numbers, - or _."
                label="Member ID"
                maxLength={20}
                name="memberId_secondary"
                placeholder="12345678900"
                regExp={MEMBER_ID_REGEXP}
                requiredErrorMsg="Field is required"
              />
            </div>
            <div className="md:col-span-3">
              <TextInput
                control={control}
                disabled={loading}
                invalidErrorMsg="Must be non-empty and using only numbers"
                label="Group Number"
                maxLength={20}
                name="groupId_secondary"
                placeholder="12345678900"
                regExp={MEMBER_ID_REGEXP}
                requiredErrorMsg="Field is required"
              />
            </div>
            <div>
              <TextInput
                control={control}
                dataTestId="rx_bin_input"
                disabled={loading}
                invalidErrorMsg="Must be non-empty and using only letters, numbers, - or _."
                label="RX BIN"
                name="rxBIN_secondary"
                placeholder="0012345"
                regExp={MEMBER_ID_REGEXP}
                requiredErrorMsg="Field is required"
              />
            </div>
            <div>
              <TextInput
                control={control}
                dataTestId="rx_group_input"
                disabled={loading}
                invalidErrorMsg="RX GROUP is invalid"
                isRequired={false}
                label="RX Group"
                name="rxGroupId_secondary"
                placeholder="A1AA"
                regExp={MEMBER_ID_REGEXP}
                requiredErrorMsg="Field is required"
              />
            </div>
            <div>
              <TextInput
                control={control}
                dataTestId="pcn_input"
                disabled={loading}
                invalidErrorMsg="PCN is invalid"
                isRequired={false}
                label="PCN"
                name="rxPCN_secondary"
                placeholder="A1"
                regExp={MEMBER_ID_REGEXP}
                requiredErrorMsg="Field is required"
              />
            </div>
          </div>
        </div>
      )}
      <div className="!max-md:sticky bottom-0 -mx-px flex flex-col items-center justify-center gap-x-3 gap-y-2  pb-4 max-md:flex-col">
        <Common.Button
          color="blue"
          disabled={disabled || !insuranceProvider || loading}
          type="submit"
          fullWidthOnMobile
        >
          {continueButtonText}
        </Common.Button>
        <Common.Button
          color="white-alt"
          disabled={loading}
          type="button"
          fullWidthOnMobile
          onClick={onSkip}
        >
          Proceed without insurance
        </Common.Button>
      </div>
    </form>
  );
};

export default AddInsuranceForm;
